import axios from 'axios';
import { API_BASE } from './apiBase';

const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
    // httpOnly session cookies are the primary auth transport (Wave 2).
    // The browser attaches `uc_admin_token` automatically; the backend
    // still accepts legacy Bearer for API tooling.
    withCredentials: true,
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Only redirect on hard 401 (session expired). No token
            // material lives in localStorage after the Wave-2 migration.
            //
            // Skip login/logout endpoints (they legitimately 401 for
            // bad credentials or when no session exists yet) and skip
            // when the user is currently on ANY /login or /portal/*
            // page — those handle unauthenticated state themselves.
            const url = error.config?.url || '';
            const path = window.location.pathname;
            // Match the /portal segment strictly — `/portal-customers` is
            // an ADMIN route that must NOT be treated as a portal page.
            const isPortalPath = path === '/portal' || path.startsWith('/portal/');
            const onAuthPage = path === '/login' || isPortalPath;
            if (!url.includes('/auth/login') && !url.includes('/auth/logout') && !onAuthPage) {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

// Auth
export const authAPI = {
    login: (data) => api.post('/auth/login', data),
    register: (data) => api.post('/auth/register', data),
    logout: () => api.post('/auth/logout'),
    getMe: () => api.get('/auth/me'),
    updateMe: (data) => api.patch('/auth/me', data),
    updatePreferredLanguage: (lang) => api.patch('/auth/me', { preferred_language: lang }),
    initAdmin: () => api.post('/init-admin'),
};

// Users
export const usersAPI = {
    getAll: () => api.get('/users'),
    create: (data) => api.post('/users', data),
    update: (id, data) => api.patch(`/users/${id}`, data),
    delete: (id) => api.delete(`/users/${id}`),
    resetPassword: (id, newPassword) =>
        api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
};

// Sales / Invoices / Drafts / Quotations (unified)
export const salesAPI = {
    create: (data) => api.post('/sales', data),
    get: (id) => api.get(`/sales/${id}`),
    getAll: (params) => api.get('/sales', { params }),
    getDrafts: (status) =>
        api.get('/sales/drafts', { params: status ? { status } : {} }),
    finalize: (id, data) => api.post(`/sales/${id}/finalize`, data),
    getDues: () => api.get('/sales/dues'),
    getCheques: (status, sortBy) => {
        const params = {};
        if (status) params.status = status;
        if (sortBy) params.sort_by = sortBy;
        return api.get('/sales/cheques', { params });
    },
    update: (id, data) => api.patch(`/sales/${id}`, data),
    updateChequeStatus: (saleId, chequeId, status) =>
        api.patch(`/sales/${saleId}/cheque/${chequeId}`, null, {
            params: { status },
        }),
    delete: (id) => api.delete(`/sales/${id}`),
    revertToDraft: (id) => api.post(`/sales/${id}/revert-to-draft`),
    nextInvoiceNumber: () => api.get('/sales/next-invoice-number'),
    nextQuotationNumber: () => api.get('/sales/next-quotation-number'),
    sendEmail: (id, payload) => api.post(`/sales/${id}/email`, payload || {}),
    packingListPdf: (id, params) =>
        api.get(`/sales/${id}/packing-list.pdf`, {
            params, responseType: 'blob',
        }),
};

// Products
export const productsAPI = {
    create: (data) => api.post('/products', data),
    getAll: (params) => api.get('/products', { params }),
    get: (id) => api.get(`/products/${id}`),
    // POS-grade multi-word tokenized search. Returns a slim projection
    // (id, sku, barcode, name, brand, price, stock_qty, image_url, unit)
    // ranked by relevance (exact > starts-with > contiguous > all-tokens).
    search: (q, opts = {}) =>
        api.get('/products/search', {
            params: { q, limit: opts.limit || 20, in_stock_only: !!opts.inStockOnly },
        }),
    // Barcode workflow
    barcodeStats: () => api.get('/products/barcode-stats'),
    missingBarcode: (params) => api.get('/products/missing-barcode', { params }),
    byBarcode: (code) => api.get(`/products/by-barcode/${encodeURIComponent(code)}`),
    assignBarcode: (id, barcode) => api.patch(`/products/${id}/barcode`, { barcode }),
    clearBarcode: (id) => api.delete(`/products/${id}/barcode`),
    update: (id, data) => api.patch(`/products/${id}`, data),
    adjustStock: (id, delta, reason) =>
        api.post(`/products/${id}/adjust-stock`, { delta, reason }),
    getStockHistory: (id, limit = 100) =>
        api.get(`/products/${id}/stock-history`, { params: { limit } }),
    delete: (id) => api.delete(`/products/${id}`),
};

// Settings — scan defaults (default qty for barcode-scan flow)
export const scanDefaultsAPI = {
    get: () => api.get('/settings/scan-defaults'),
    put: (default_quantity) => api.put('/settings/scan-defaults', { default_quantity }),
};

// Customers
export const customersAPI = {
    create: (data) => api.post('/customers', data),
    getAll: (params) => api.get('/customers', { params }),
    update: (id, data) => api.patch(`/customers/${id}`, data),
    delete: (id) => api.delete(`/customers/${id}`),
    nextCode: () => api.get('/customers/next-code'),
    checkDuplicates: (data) => api.post('/customers/check-duplicates', data),
};

// Address autocomplete (BAN)
export const addressAPI = {
    autocomplete: (q, limit = 6) =>
        api.get('/address/autocomplete', { params: { q, limit } }),
};

// Brands
export const brandsAPI = {
    create: (data) => api.post('/brands', data),
    getAll: () => api.get('/brands'),
    update: (id, data) => api.patch(`/brands/${id}`, data),
    delete: (id) => api.delete(`/brands/${id}`),
    nextSku: (id) => api.get(`/brands/${id}/next-sku`),
};

// Categories
export const categoriesAPI = {
    create: (data) => api.post('/categories', data),
    getAll: () => api.get('/categories'),
    update: (id, data) => api.patch(`/categories/${id}`, data),
    delete: (id) => api.delete(`/categories/${id}`),
};

// Suppliers
export const suppliersAPI = {
    create: (data) => api.post('/suppliers', data),
    getAll: (params) => api.get('/suppliers', { params }),
    update: (id, data) => api.patch(`/suppliers/${id}`, data),
    delete: (id) => api.delete(`/suppliers/${id}`),
    cardPdf: (id) => api.get(`/suppliers/${id}/card.pdf`, { responseType: 'blob' }),
};

// Inventory batches
export const batchesAPI = {
    create: (data) => api.post('/batches', data),
    getAll: (params) => api.get('/batches', { params }),
    get: (id) => api.get(`/batches/${id}`),
    update: (id, data) => api.patch(`/batches/${id}`, data),
    delete: (id) => api.delete(`/batches/${id}`),
    recall: (id, reason) => api.post(`/batches/${id}/recall`, { reason }),
    affectedCustomers: (id) => api.get(`/batches/${id}/affected-customers`),
    forProduct: (productId, only_active = true) =>
        api.get(`/products/${productId}/batches`, { params: { only_active } }),
};

// Expiry dashboard
export const expiryAPI = {
    summary: () => api.get('/expiry/summary'),
    list: (bucket) => api.get('/expiry', { params: bucket ? { bucket } : {} }),
};

// Settings (default warehouse, etc.)
export const settingsAPI = {
    getWarehouse: () => api.get('/settings/warehouse'),
    setWarehouse: (warehouse) => api.put('/settings/warehouse', { warehouse }),
};

// Alerts
export const alertsAPI = {
    get: () => api.get('/alerts'),
    sendDueReminder: () => api.post('/alerts/send-due-reminder'),
    sendLowStock: () => api.post('/alerts/send-low-stock'),
    emailStatus: () => api.get('/alerts/email-status'),
    sendTestEmail: (to) => api.post('/alerts/send-test-email', { to }),
};

// Dashboard
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getChartData: (period) => api.get('/dashboard/chart-data', { params: { period } }),
    getPaymentBreakdown: (period) =>
        api.get('/dashboard/payment-breakdown', { params: { period } }),
};

// Reports
export const reportsAPI = {
    get: (startDate, endDate) =>
        api.get('/reports', { params: { start_date: startDate, end_date: endDate } }),
    salesAnalytics: (params) => api.get('/reports/sales-analytics', { params }),
    inventoryAnalytics: (params) => api.get('/reports/inventory-analytics', { params }),
    customerStatement: (params) => api.get('/reports/customer-statement', { params }),
    purchaseAnalytics: (params) => api.get('/reports/purchase-analytics', { params }),
    vatAnalytics: (params) => api.get('/reports/vat-analytics', { params }),
    auditLog: (limit = 100) => api.get('/reports/audit-log', { params: { limit } }),
    emailCustomerStatement: (payload) =>
        api.post('/reports/customer-statement/email', payload),
};

export const catalogAPI = {
    // Accepts URLSearchParams or plain object. Passing URLSearchParams
    // preserves repeated keys (?brand_ids=A&brand_ids=B), which FastAPI
    // List[str] query params require.
    products: (params) => {
        const url = params instanceof URLSearchParams
            ? `/catalog/products?${params.toString()}`
            : '/catalog/products';
        return params instanceof URLSearchParams
            ? api.get(url)
            : api.get('/catalog/products', { params });
    },
    exportPdf: (payload) =>
        api.post('/catalog/export.pdf', payload, { responseType: 'blob' }),
};

export const cloudOrdersAPI = {
    list: (params) => api.get('/cloud-orders', { params }),
    get: (id) => api.get(`/cloud-orders/${id}`),
    approve: (id) => api.post(`/cloud-orders/${id}/approve`),
    reject: (id, payload) => api.post(`/cloud-orders/${id}/reject`, payload),
};

export const portalCustomersAdminAPI = {
    list: (params) => api.get('/portal-customers', { params }),
    approve: (email) => api.post(`/portal-customers/${encodeURIComponent(email)}/approve`),
    quickLink: (email) => api.post(`/portal-customers/${encodeURIComponent(email)}/quick-link`),
    suspend: (email) => api.post(`/portal-customers/${encodeURIComponent(email)}/suspend`),
    setPricing: (email, can_see_prices) =>
        api.patch(`/portal-customers/${encodeURIComponent(email)}/pricing`, { can_see_prices }),
    impersonate: (email) => api.post('/portal/auth/admin/impersonate', { email }),
};

export const healthAPI = {
    shallow: () => api.get('/health'),
    deep: () => api.get('/health/deep'),
};

export default api;
